import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Eye, FileDown, Printer, Pencil } from 'lucide-react';
import useBranchLabels from '../../hooks/useBranchLabels';

export default function StudentSelectTable({
  students,
  loading,
  search,
  onSearchChange,
  selectedIds,
  allSelected,
  onToggleAll,
  onToggleOne,
  onPreview,
  onPdf,
  onPrint,
  onEditTc,
  showTcEdit = false,
  showFeePending = false,
  isActionDisabled,
  actionDisabledTitle = 'Action disabled',
  pdfPrintDisabled = false,
}) {
  const { formatLabel } = useBranchLabels();
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="p-3 border-b border-border flex flex-wrap gap-3 items-center justify-between bg-muted/20">
        <Input
          type="search"
          className="max-w-xs"
          placeholder="Search name, admission no, roll…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">{students.length} student(s)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="p-3 w-10">
                <Checkbox
                  checked={allSelected && students.length > 0}
                  onCheckedChange={(c) => onToggleAll(Boolean(c), students.map((s) => s.id))}
                />
              </th>
              <th className="p-3 text-left">Roll</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Admission No</th>
              <th className="p-3 text-left">Section</th>
              <th className="p-3 text-left">Status</th>
              {showFeePending && <th className="p-3 text-right">Fee pending</th>}
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showFeePending ? 8 : 7} className="p-8 text-center text-muted-foreground">Loading students…</td></tr>
            ) : students.length === 0 ? (
              <tr><td colSpan={showFeePending ? 8 : 7} className="p-8 text-center text-muted-foreground">Select a class to load students.</td></tr>
            ) : (
              students.map((s) => {
                const name = [s.first_name, s.last_name].filter(Boolean).join(' ');
                const feeBlocked = typeof isActionDisabled === 'function' ? isActionDisabled(s) : false;
                const pending = Number(s.fee_pending ?? 0);
                return (
                  <tr key={s.id} className="border-t border-border hover:bg-muted/20">
                    <td className="p-3">
                      <Checkbox
                        checked={selectedIds.includes(s.id)}
                        onCheckedChange={() => onToggleOne(s.id)}
                      />
                    </td>
                    <td className="p-3">{s.roll_no || '—'}</td>
                    <td className="p-3 font-medium">{formatLabel(name, s)}</td>
                    <td className="p-3">{s.admission_no || '—'}</td>
                    <td className="p-3">{s.section_name || '—'}</td>
                    <td className="p-3 capitalize">{s.status || '—'}</td>
                    {showFeePending && (
                      <td className={`p-3 text-right ${pending > 0.01 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                        {pending > 0.01 ? `₹${pending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'Cleared'}
                      </td>
                    )}
                    <td className="p-3">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {showTcEdit && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => onEditTc?.(s)} title="Edit TC fields">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={feeBlocked}
                          onClick={() => onPreview(s)}
                          title={feeBlocked ? actionDisabledTitle : 'Preview'}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={pdfPrintDisabled || feeBlocked}
                          onClick={() => onPrint(s)}
                          title={feeBlocked ? actionDisabledTitle : (pdfPrintDisabled ? 'Loading school settings…' : 'Print')}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={pdfPrintDisabled || feeBlocked}
                          onClick={() => onPdf(s)}
                          title={feeBlocked ? actionDisabledTitle : (pdfPrintDisabled ? 'Loading school settings…' : 'Download PDF')}
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
